import { isValidElement } from 'react'
import { isDevelopment } from '~/components/jsonschema/VndlyJsonSchemaFormBase/actions/utils'
import {
  Alert,
  AlertIcon,
  Box,
  Collapse,
  Flex,
  Heading,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  EmptyState,
  CaughtUpGraphic,
  RefreshErrorGraphic,
  RegionErrorBoundary
} from '~/design_system'

/**
 * Example Usage:
 * Each state has a `when` predicate and a `render` method.
 *
 * @param props {Object}
 * @param props.states {Array<{ when: (boolean | () => boolean); render: () => React.ReactNode}>} - An array of state objects, each with a `when` predicate and a `render` method.
 * @param props.children {React.ReactNode | (() => React.ReactNode)} - The content to render when no sad state is active.
 *
 * @example
 * ```jsx
 * <SadStates
 *  states={[
 *    {
 *      when: () => !selection,
 *      render: () => <p>😞Please make a selection</p>
 *    },
 *    { when: () => !data,
 *      render: () => <p>😞There is no data for this selection</p>
 *    }
 *  ]}>
 *    <p>😄There is data for this selection!</p>
 *  </SadStates>
 *  ```
 *
 * @agent-transitional EmptyState from ~/design_system for empty states; PageErrorState remains legacy for errors
 */
export const SadStates = ({ states = [], children = null, ...props }) => {
  const sadState = states.find(c => (typeof c.when === 'function' ? c.when() : c.when))

  if (sadState) {
    return <RegionErrorBoundary {...props}>{sadState.render()}</RegionErrorBoundary>
  }

  return <RegionErrorBoundary {...props}>{typeof children === 'function' ? children() : children}</RegionErrorBoundary>
}

const spinnerConfig = {
  small: {
    size: 'md'
  },
  large: {
    size: 'xl'
  },
  default: {
    size: 'lg'
  }
}

export const LoadingState = ({ size: spinnerSize }) => {
  const { size } = spinnerConfig[spinnerSize] || spinnerConfig.default
  return (
    <Flex height="100%" alignItems="center" justifyContent="center">
      <Spinner size={size} />
    </Flex>
  )
}

export const SectionLoadingState = () => <LoadingState size="small" />

export const PageSadStateBox = props => (
  <Stack textAlign="center" bg="var(--vndly-color-container-background-subdued)" padding={6} mt={4} {...props} />
)
export const PageSadStateDefaultIcon = props => (
  <Box w="75px" h="75px" mx="auto" color="var(--vndly-color-container-border)" {...props} />
)
export const PageSadStateHeading = props => <Heading as="h3" textStyle="heading3" {...props} />

/**
 * @deprecated This component has been replaced by our various EmptyState components as part of the DS,
 * please reference https://storybook.beta.vndly.com/?path=/docs/components-indicator-empty-states--docs#no-results
 *
 * @param {{
 *    heading: React.ReactNode,
 *    content?: React.ReactNode | null,
 *    icon?: any,
 *    happy?: boolean,
 * } | import('@chakra-ui/react').StackProps} props
 */
export const PageNoResultsState = ({ heading, content = null, icon = null, happy = false, ...rest }) => (
  <EmptyState {...rest}>
    {(() => {
      if (isValidElement(icon)) return icon
      if (icon) return <PageSadStateDefaultIcon as={icon} />
      if (happy) return <CaughtUpGraphic />
      return <RefreshErrorGraphic />
    })()}

    <Text textStyle="bodySemi">{heading}</Text>
    <Text>{content}</Text>
  </EmptyState>
)

/**
 * @deprecated This component has been replaced by our new RefreshError component as part of the DS,
 * please reference https://storybook.beta.vndly.com/?path=/docs/components-indicator-empty-states--docs#refresh-or-error
 */
export const PageErrorState = ({ message, icon = null, iconSize = 'lg', raw = null, ...rest }) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: isDevelopment() })
  return (
    <>
      <Alert status="error" justifyContent="center" {...rest}>
        {icon ? (
          <Box as={icon} fontSize={iconSize} color="var(--vndly-color-container-icon-danger)" mr={3} />
        ) : (
          <AlertIcon onClick={onToggle} />
        )}
        {message}
      </Alert>
      {raw && (
        // If additional `raw` error details are provided they will be shown
        // in development, or can be viewed in production by clicking the AlertIcon
        <Collapse in={isOpen}>
          <Alert status="error">
            <Box
              as="pre"
              display="block"
              whiteSpace="pre-wrap"
              w="full"
              bg="gray.100"
              p={3}
              rounded={2}
              border="1px"
              borderColor="red.200"
              fontSize="sm">
              {raw}
            </Box>
          </Alert>
        </Collapse>
      )}
    </>
  )
}
